/**
 * Las personas invitadas a una aplicación.
 *
 * Cada aplicación tiene sus propias cuentas; aquí viajan las suyas, para poder
 * pintar las columnas que apuntan a la tabla de personas.
 *
 * Es de las pocas cosas que siguen siendo contexto y no un módulo con estado:
 * hay una lista por aplicación, no una sola para todo el panel.
 */
import type { AppPerson } from "@shared/types";
import { getContext, setContext } from "svelte";

import { api, pbApp } from "./pb";

/** Lo que se guarda en el contexto: un objeto que sabe leer su propia lista. */
interface PeopleSource {
  readonly list: AppPerson[];
}

const KEY = Symbol("plane:personas");

/** Pone la lista de personas invitadas en el contexto para los componentes de más abajo. */
export function setPeople(source: PeopleSource): void {
  setContext(KEY, source);
}

/**
 * Quien está invitado, para quien lo necesite más abajo.
 *
 * Devuelve la fuente, no la lista: `personas.list` se lee cuando se dibuja, y
 * así el cambio llega. Sacar el array aquí lo congelaria en el valor que
 * tuviera al montar.
 */
export function getPeople(): PeopleSource {
  return getContext<PeopleSource | undefined>(KEY) ?? { list: [] };
}

/**
 * Lista para el panel: quien construye ve a los invitados de su app.
 *
 * Devuelve también como volver a pedirla. Hace falta: invitar a alguien no pasa
 * por aquí --pasa por la tabla de personas-- y sin releer, las celdas que
 * acaban de enlazarse con esa persona la buscarian en una lista donde todavía
 * no está y se pintarian como enlaces rotos.
 */
export function appPeople(appId: () => string | undefined): PeopleSource & {
  reload: () => Promise<void>;
} {
  let list = $state<AppPerson[]>([]);
  let turn = 0;

  /**
   * Carga la lista de personas y decide si conservar la anterior cuando falla mediante
   * keep. Al refrescar la conserva para no contar como vacío un fallo de red antes de
   * retirar roles.
   *
   * Al cambiar de aplicación la descarta para no mostrar personas de otra aplicación.
   */
  async function load(keep = false) {
    const id = appId();
    const mine = ++turn;
    if (!keep) list = [];
    if (!id) return;
    try {
      const rows = await api<AppPerson[]>(`/api/apps/${id}/personas`);
      // Una respuesta de otra aplicación o de una recarga anterior ya no sirve.
      if (mine === turn && id === appId()) list = rows;
    } catch {
      if (!keep && mine === turn && id === appId()) list = [];
    }
  }

  $effect(() => {
    void appId();
    void load();
    return () => {
      turn++;
    };
  });

  return {
    get list() {
      return list;
    },
    reload: () => load(true),
  };
}

/** Lista para la app publicada: solo llega con una sesión válida. */
export function publishedPeople(slug: () => string, signedIn: () => boolean): PeopleSource {
  let list = $state<AppPerson[]>([]);

  $effect(() => {
    const name = slug();
    if (!name || !signedIn()) {
      list = [];
      return;
    }
    let alive = true;
    const stop = new AbortController();
    list = [];
    fetch(`/api/public/${encodeURIComponent(name)}/personas`, {
      headers: { authorization: pbApp.authStore.token },
      signal: stop.signal,
    })
      .then(async (res) => {
        if (res.ok) return res.json();
        // Un rechazo no se lee, pero su cuerpo también debe liberarse.
        await res.body?.cancel();
        return [];
      })
      .then((rows) => {
        if (alive) list = Array.isArray(rows) ? rows : [];
      })
      .catch(() => {
        if (alive) list = [];
      });
    return () => {
      alive = false;
      // Al salir ya no hay quien use la respuesta ni la lista que pueda traer.
      stop.abort();
    };
  });

  return {
    get list() {
      return list;
    },
  };
}
